<template>
    <VNavigationDrawer v-model="link.secureMainDrawer" order="0" color="transparent" floating flat width="280">
        <VCard :style="backgroundStyle" class="fill-height" rounded="0">
            <VContainer v-if="store.layoutVertical === true" style="height: 64px"
                class="d-flex align-center justify-center">
                <AppLogo />
            </VContainer>

            <VList density="compact" nav>
                <template v-for="group in menuGroups" :key="group.header">
                    <VListSubheader>{{ group.header }}</VListSubheader>

                    <template v-for="item in group.items" :key="item.id">
                        <!-- Items with submenu -->
                        <VListGroup v-if="item.menu && item.subItems?.length" :value="item.text">
                            <template #activator="{ props }">
                                <VListItem v-bind="props" color="primary" :title="item.text" :prepend-icon="item.icon"
                                    :disabled="item.disabled" v-ripple="{ class: 'text-primary' }"
                                    @mouseover="activeHover(item.id)" @mouseleave="clearHover" />
                            </template>

                            <VListItem v-for="sub in item.subItems" :key="sub.id" color="primary" density="compact"
                                :title="sub.text" :to="sub.to" :prepend-icon="sub.icon" :disabled="sub.disabled"
                                v-ripple="{ class: 'text-primary' }" @mouseover="activeHover(sub.id)"
                                @mouseleave="clearHover" />
                        </VListGroup>

                        <!-- Normal items -->
                        <VListItem v-else color="primary" :title="item.text" :to="item.to" :prepend-icon="item.icon"
                            :disabled="item.disabled" exact v-ripple="{ class: 'text-primary' }"
                            @mouseover="activeHover(item.id)" @mouseleave="clearHover">
                            <!-- Tickets badge -->
                            <template v-if="item.text === 'Tickets' && userData.openTicketCount !== 0" #append>
                                <VChip :color="userData.openTicketCount > 0 ? 'primary' : 'grey'" size="small"
                                    :variant="theme.global.name.value === 'dark' ? 'tonal' : 'flat'"
                                    class="d-flex align-center justify-center mr-2">
                                    <span
                                        :class="`${(userData?.openTicketCount ?? 0) > 0 ? 'font-weight-bold' : 'font-weight-medium'} text-button`">
                                        {{ userData.openTicketCount }}
                                    </span>
                                </VChip>
                            </template>
                        </VListItem>
                    </template>
                </template>
            </VList>
        </VCard>

        <!-- Drawer footer button to open the full admin management dialog -->
        <template v-if="userData.user.role === 'admin'" #append>
            <VCardText :style="backgroundStyle" class="pb-2 pt-2">
                <VBtn color="primary" variant="outlined" block @click="openAdminOptionsDialog">
                    Management
                </VBtn>
            </VCardText>
        </template>
    </VNavigationDrawer>

    <!-- Admin Management dialog with ALL admin items -->
    <VDialog v-if="userData.user.role === 'admin'" v-model="showAdminDialog" max-width="520" scrollable>
        <VCard :style="backgroundStyle">
            <VCol class="pt-2 px-2 d-flex align-center pa-0" cols="12">
                <VCardTitle>{{ smAndUp ? 'Admin Management' : 'Admin Mgmt.' }}</VCardTitle>
                <VSpacer />
                <VIcon class="mr-2" style="cursor: pointer" icon="$mdiArrowTopRight" size="large"
                    :style="{ filter: 'brightness(.8)', transition: 'filter 0.3s ease' }"
                    @mouseenter="$event.target.style.filter = 'brightness(1)'"
                    @mouseleave="$event.target.style.filter = 'brightness(.8)'" @click="navigateTo('/admin')" />
            </VCol>

            <VCardText class="pt-0">
                <VList density="compact" nav style="background: transparent">
                    <template v-for="group in adminMenuGroups" :key="group.header">
                        <VListSubheader>{{ group.header }}</VListSubheader>

                        <VListItem v-for="item in group.items" :key="item.id" :title="item.text" :to="item.to"
                            :prepend-icon="item.icon" color="primary" v-ripple="{ class: 'text-primary' }"
                            @click="closeAdminOptionsDialog" @mouseover="activeHover(item.id)"
                            @mouseleave="clearHover" />
                    </template>
                </VList>
            </VCardText>
        </VCard>
    </VDialog>
</template>

<script setup lang="ts">
import { useTheme, useDisplay } from 'vuetify'
import { useAppStore } from '@S/appStore.ts'
import { useAppLink } from '@S/appLink.ts'
import { useUserDataStore } from '@S/userDataStore.ts'
import { useBackgroundStyle } from '@R/constants/componentStyle.ts'
import { secureDrawerRoutes, adminRoutes } from '@U/navigationGroups.ts'

const store = useAppStore()
const link = useAppLink()
const userData = useUserDataStore()

const backgroundStyle = useBackgroundStyle()
const { mdAndDown, smAndUp } = useDisplay()
const theme = useTheme()

const hoveredItem = ref<number | null>(null)
const showAdminDialog = ref(false)

function activeHover(id: number) {
    hoveredItem.value = id
}
function clearHover() {
    hoveredItem.value = null
}

function openAdminOptionsDialog() {
    showAdminDialog.value = true
}
function closeAdminOptionsDialog() {
    showAdminDialog.value = false
}


const menuGroups = computed(() => secureDrawerRoutes)


const adminMenuGroups = computed(() => adminRoutes)



onBeforeMount(() => {
    if (mdAndDown.value) link.secureMainDrawer = false
})
</script>
